/**
 * Central, typed query-key registry (ARCHITECTURE.md §3). Every resource hook
 * derives its keys here so invalidation stays consistent and refactors are safe.
 * Grows one block per feature as phases land.
 */
export const queryKeys = {
  account: {
    all: ["account"] as const,
    me: () => [...queryKeys.account.all, "me"] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    counts: () => [...queryKeys.notifications.all, "counts"] as const,
  },
  auth: {
    all: ["auth"] as const,
    signupConfig: () => [...queryKeys.auth.all, "signup-config"] as const,
    captcha: () => [...queryKeys.auth.all, "captcha"] as const,
  },
  dashboard: {
    all: ["dashboard"] as const,
    summary: () => [...queryKeys.dashboard.all, "summary"] as const,
  },
  siteConfig: {
    all: ["site-config"] as const,
  },
  profile: {
    all: ["profile"] as const,
    me: () => [...queryKeys.profile.all, "me"] as const,
    user: (userId: string) =>
      [...queryKeys.profile.all, "user", userId] as const,
  },
  payments: {
    all: ["payments"] as const,
    transactions: (group: string, page: number) =>
      [...queryKeys.payments.all, "transactions", group, page] as const,
  },
  workers: {
    all: ["workers"] as const,
    list: (limit: number) => [...queryKeys.workers.all, "list", limit] as const,
    /** One filtered/sorted page of the directory — every field is part of the
     * key, since the backend (not the client) applies them. */
    page: (query: object) => [...queryKeys.workers.all, "page", query] as const,
    search: (query: string, limit: number) =>
      [...queryKeys.workers.all, "search", query, limit] as const,
    top: () => [...queryKeys.workers.all, "top"] as const,
    mine: () => [...queryKeys.workers.all, "mine"] as const,
    hireJobs: () => [...queryKeys.workers.all, "hire-jobs"] as const,
  },
  referrals: {
    all: ["referrals"] as const,
    employer: ["referrals", "employer"] as const,
  },
  support: {
    all: ["support"] as const,
    tickets: (page: number) =>
      [...queryKeys.support.all, "tickets", page] as const,
    chat: (id: string) => [...queryKeys.support.all, "chat", id] as const,
  },
  /** Direct messaging — distinct from `support.chat` (ticket threads). */
  chat: {
    all: ["chat"] as const,
    conversations: () => [...queryKeys.chat.all, "conversations"] as const,
    messages: (conversationId: string) =>
      [...queryKeys.chat.all, "messages", conversationId] as const,
    unread: () => [...queryKeys.chat.all, "unread"] as const,
  },
  reports: {
    all: ["reports"] as const,
    jobTitle: (id: string) =>
      [...queryKeys.reports.all, "job-title", id] as const,
  },
  developers: {
    all: ["developers"] as const,
    token: () => [...queryKeys.developers.all, "token"] as const,
  },
  jobs: {
    all: ["jobs"] as const,
    search: (params: unknown) =>
      [...queryKeys.jobs.all, "search", params] as const,
    detail: (id: string) => [...queryKeys.jobs.all, "detail", id] as const,
    apply: (id: string) => [...queryKeys.jobs.all, "apply", id] as const,
    platformStats: () => [...queryKeys.jobs.all, "platform-stats"] as const,
    invitations: () => [...queryKeys.jobs.all, "invitations"] as const,
    offers: () => [...queryKeys.jobs.all, "offers"] as const,
    finished: (page: number) =>
      [...queryKeys.jobs.all, "finished", page] as const,
    finishedTask: (id: string) =>
      [...queryKeys.jobs.all, "finished-task", id] as const,
    mine: (filter: string, page: number) =>
      [...queryKeys.jobs.all, "mine", filter, page] as const,
    sharedWithMe: (page: number) =>
      [...queryKeys.jobs.all, "shared-with-me", page] as const,
    applicants: (jobId: string, query: unknown) =>
      [...queryKeys.jobs.all, "applicants", jobId, query] as const,
    applicantTask: (jobId: string, taskId: string) =>
      [...queryKeys.jobs.all, "applicant-task", jobId, taskId] as const,
    sharing: (jobId: string) =>
      [...queryKeys.jobs.all, "sharing", jobId] as const,
    buckets: (jobId: string, page: number) =>
      [...queryKeys.jobs.all, "buckets", jobId, page] as const,
    plannedTasks: (jobId: string, query: unknown) =>
      [...queryKeys.jobs.all, "planned-tasks", jobId, query] as const,
  },
  devices: {
    all: ["devices"] as const,
    list: (page: number, pageSize?: number) =>
      pageSize === undefined
        ? ([...queryKeys.devices.all, "list", page] as const)
        : ([...queryKeys.devices.all, "list", page, pageSize] as const),
    successRates: (params: unknown) =>
      [...queryKeys.devices.all, "success-rates", params] as const,
    loadStats: (period: string) =>
      [...queryKeys.devices.all, "load-stats", period] as const,
    market: (params: unknown) =>
      [...queryKeys.devices.all, "market", params] as const,
    settings: (deviceId: string) =>
      [...queryKeys.devices.all, "settings", deviceId] as const,
    triggerLogs: (deviceId: string, page: number) =>
      [...queryKeys.devices.all, "trigger-logs", deviceId, page] as const,
    bucket: (remoteDeviceId: string) =>
      [...queryKeys.devices.all, "bucket", remoteDeviceId] as const,
    proxyConnections: () =>
      [...queryKeys.devices.all, "proxy-connections"] as const,
    proxyBlacklist: (connectionId: string) =>
      [...queryKeys.devices.all, "proxy-blacklist", connectionId] as const,
    notifications: (page: number) =>
      [...queryKeys.devices.all, "notifications", page] as const,
  },
  automations: {
    all: ["automations"] as const,
    list: () => [...queryKeys.automations.all, "list"] as const,
    detail: (id: string) =>
      [...queryKeys.automations.all, "detail", id] as const,
    files: (id: string) => [...queryKeys.automations.all, "files", id] as const,
    file: (id: string, path: string) =>
      [...queryKeys.automations.all, "file", id, path] as const,
    gitStatus: (id: string) =>
      [...queryKeys.automations.all, "git-status", id] as const,
    gitHistory: (id: string) =>
      [...queryKeys.automations.all, "git-history", id] as const,
    gitCommitDiff: (id: string, hash: string) =>
      [...queryKeys.automations.all, "git-commit-diff", id, hash] as const,
    gitDiffWorking: (id: string, hash: string) =>
      [...queryKeys.automations.all, "git-diff-working", id, hash] as const,
    gitDiffCommits: (id: string, from: string, to: string) =>
      [...queryKeys.automations.all, "git-diff-commits", id, from, to] as const,
    perfFilters: (range: unknown) =>
      [...queryKeys.automations.all, "perf-filters", range] as const,
    perfData: (params: unknown) =>
      [...queryKeys.automations.all, "perf-data", params] as const,
    /** Prefix for every out-of-steps list, whatever its filters. */
    outOfStepsAll: () =>
      [...queryKeys.automations.all, "out-of-steps"] as const,
    outOfSteps: (filters: unknown) =>
      [...queryKeys.automations.outOfStepsAll(), filters] as const,
    oosProgressStats: (params: unknown) =>
      [...queryKeys.automations.all, "oos-progress-stats", params] as const,
    oosProgressUsers: (params: unknown) =>
      [...queryKeys.automations.all, "oos-progress-users", params] as const,
    uiNodes: (url: string) =>
      [...queryKeys.automations.all, "ui-nodes", url] as const,
  },
  labelling: {
    all: ["labelling"] as const,
    history: (type: string, page: number) =>
      [...queryKeys.labelling.all, "history", type, page] as const,
    apps: () => [...queryKeys.labelling.all, "apps"] as const,
  },
  admin: {
    all: ["admin"] as const,
    applicants: (params: unknown) =>
      [...queryKeys.admin.all, "applicants", params] as const,
    applicantTask: (taskId: string) =>
      [...queryKeys.admin.all, "applicant-task", taskId] as const,
    reports: (page: number) =>
      [...queryKeys.admin.all, "reports", page] as const,
    report: (reportId: string) =>
      [...queryKeys.admin.all, "report", reportId] as const,
  },
} as const;
